/**
 * The documentation served at `/doc`.
 *
 * Content is Markdown on disk, imported raw and bundled with the app — no database, no API, no
 * fetch. That is deliberate: these pages are instructions about the product, they change with the
 * product, and they have to render for a reader who is not signed in.
 *
 * **This is a curated, publicly shareable surface.** Nothing is documented here unless it has been
 * asked for by name. See the note in CLAUDE.md before adding to it.
 */

import type { UiFeature } from '@repo/shared-types';
import activity from './activity.md?raw';
import automationFlow from './automation-flow.md?raw';
import automations from './automations.md?raw';
import connectClaudeAndChatgpt from './connect-claude-and-chatgpt.md?raw';
import feed from './feed.md?raw';
import gettingStarted from './getting-started.md?raw';
import globalIngestion from './global-ingestion.md?raw';
import sources from './sources.md?raw';
import retrievalFlow from './retrieval-flow.md?raw';
import sweepFlow from './sweep-flow.md?raw';
import knowledgePages from './knowledge-pages.md?raw';
import mcpTools from './mcp-tools.md?raw';
import kbReview from './kb-review.md?raw';
import kbSweeps from './kb-sweeps.md?raw';
import projects from './projects.md?raw';

/**
 * `guide` is how to use the product; `engineering` is how it works underneath.
 *
 * The second was `explainer`, held open and empty for exactly this — a group with no entries simply
 * isn't rendered, so filling it in cost no restructuring. Renamed along with it because the audience
 * is different: these pages are for whoever has to change the system, and they name files, columns
 * and environment variables that an explainer written for a user would not.
 */
export type DocGroup = 'guide' | 'engineering';

export interface DocEntry {
  /** URL segment under `/doc/`. */
  slug: string;
  /** Title in the nav and at the head of the page. Should match the document's own `# ` heading. */
  title: string;
  /** One line, shown under the title in the nav index. */
  blurb: string;
  group: DocGroup;
  content: string;
  /**
   * The console section this guide documents. A reader who has that section switched off does not
   * see the guide either — in the nav, the index, the pager, or at its URL — because a guide to a
   * page you cannot open is a description of somebody else's console.
   *
   * Absent means everyone: the engineering group, and the guides about things every account has
   * (getting started, projects, connecting an assistant). A signed-out reader resolves every flag to
   * on, the same default the console uses, so the public surface is unchanged.
   */
  feature?: UiFeature;
}

export const DOC_GROUPS: Array<{ group: DocGroup; label: string; description: string }> = [
  {
    group: 'guide',
    label: 'User guide',
    description: 'How to do the things the console is for.',
  },
  {
    group: 'engineering',
    label: 'Engineering',
    description: 'How the system works underneath, for whoever has to change it.',
  },
];

/** Order here is the order the nav renders and the index lists. */
export const DOCS: DocEntry[] = [
  // First, and the only entry that assumes no prior knowledge: it walks the path once end to end —
  // project, source, batch, run, pages, automation — and hands off to the guides below rather than
  // restating them.
  {
    slug: 'getting-started',
    title: 'Getting started',
    blurb: 'Create a project, connect a source, follow one batch through to the pages it writes, and tell it when it got something wrong.',
    group: 'guide',
    content: gettingStarted,
  },
  {
    slug: 'projects',
    title: 'Projects, sub-projects and the overview',
    blurb: 'Create a project, nest sub-projects under it, and read what a project overview is telling you.',
    group: 'guide',
    content: projects,
  },
  {
    slug: 'knowledge-pages',
    title: 'Knowledge pages',
    blurb: 'The pages inside a project: creating them, editing them, and letting the agent write them.',
    group: 'guide',
    content: knowledgePages,
    feature: 'KB',
  },
  {
    slug: 'kb-review',
    title: 'KB Review',
    blurb: 'The review queue: why a change is waiting for you, and the ways to answer it.',
    group: 'guide',
    content: kbReview,
    feature: 'KB_REVIEW',
  },
  // Its own page since it became its own section with its own flag: who may watch the agents work
  // is a different question from who reviews what they wrote.
  {
    slug: 'activity',
    title: 'Activity',
    blurb: 'Every ingestion run: what it was handed, where it placed it, what it changed and what it cost.',
    group: 'guide',
    content: activity,
    feature: 'ACTIVITY',
  },
  // Immediately after the review queue, because the pair is the point: the queue judges one edit as
  // it is made, sweeps judge what the store has become. Read apart, each looks like it ought to
  // cover the other's job.
  {
    slug: 'kb-sweeps',
    title: 'KB Sweeps',
    blurb: 'An agent that reads the whole store and says what is wrong with it, and the thread on each card that hands the fix back.',
    group: 'guide',
    content: kbSweeps,
    feature: 'KB_QUALITY',
  },
  {
    slug: 'automations',
    title: 'Automations',
    blurb: 'Writing a prompt, choosing a trigger, and managing what an automation produces.',
    group: 'guide',
    content: automations,
    feature: 'AUTOMATIONS',
  },
  // Before Sources: what arrived is the question people come with, and where it came from follows.
  {
    slug: 'feed',
    title: 'Feed',
    blurb: 'Everything that arrived, how far each item has got on its way into memory, and searching it as an automation would.',
    group: 'guide',
    content: feed,
    feature: 'FEED',
  },
  {
    slug: 'sources',
    title: 'Sources',
    blurb: 'What the knowledge base receives from the sources hub and Slack, where each source goes, and what is waiting.',
    group: 'guide',
    content: sources,
    feature: 'GLOBAL_INTEGRATIONS',
  },
  // Last because it is the step after there is something to read: pointing an assistant at the
  // knowledge base rather than working inside the console.
  {
    slug: 'connect-claude-and-chatgpt',
    title: 'Connecting Claude and ChatGPT',
    blurb: 'Attach the knowledge base to an assistant with one URL, and what it can do once connected.',
    group: 'guide',
    content: connectClaudeAndChatgpt,
  },
  // Immediately after the connection guide, because it is the question that follows it: the guide
  // gets an assistant attached, this says what it can then be asked to do. Kept separate rather than
  // folded in — a tool reference read once is a different document from setup instructions read once.
  {
    slug: 'mcp-tools',
    title: 'The MCP tools',
    blurb: 'What a connected assistant can actually do: the read ladder, the two ways to save, automations, and how to ask for them.',
    group: 'guide',
    content: mcpTools,
  },
  // The engineering group is one page per agent loop, in the order content moves through them:
  // something arrives and is written down, something is asked of what was written, the store as a
  // whole is judged, and an agent reads it on a schedule. Each follows one subject from the door it
  // enters through to the row that records what happened, with the reason for each rule where the
  // rule is stated.
  {
    slug: 'global-ingestion',
    title: 'The global ingestion flow',
    blurb:
      'One arrival end to end: the single door, the batch window, the write gate, and the two phases of an ingestor run.',
    group: 'engineering',
    content: globalIngestion,
  },
  {
    slug: 'retrieval-flow',
    title: 'The retrieval flow',
    blurb:
      'One question end to end: the read scope, the eight tools, page and section search, evidence and entities, the citation guarantee, and the fallback.',
    group: 'engineering',
    content: retrievalFlow,
  },
  {
    slug: 'sweep-flow',
    title: 'The sweep flow',
    blurb:
      'The agent that judges the store: thirteen tools, four lenses, the fingerprinted card ledger, and how an instruction on a card becomes confined ingestion runs.',
    group: 'engineering',
    content: sweepFlow,
  },
  {
    slug: 'automation-flow',
    title: 'The automation flow',
    blurb:
      'A prompt plus a trigger: eleven triggers, the fixed roster, delivery outside the loop, and what comes back — replies, Doc edits and freshness.',
    group: 'engineering',
    content: automationFlow,
  },
];

export function docBySlug(slug: string | undefined): DocEntry | null {
  if (!slug) return null;
  return DOCS.find((doc) => doc.slug === slug) ?? null;
}

/** Entries in one group, in declaration order. Empty groups are the caller's business to skip. */
export function docsInGroup(group: DocGroup, docs: DocEntry[] = DOCS): DocEntry[] {
  return docs.filter((doc) => doc.group === group);
}

/** The documents this reader may see — every one whose section they have, plus the ungated ones. */
export function visibleDocs(hasFeature: (feature: UiFeature) => boolean): DocEntry[] {
  return DOCS.filter((doc) => !doc.feature || hasFeature(doc.feature));
}

/**
 * Anchor id for a heading, matching what the renderer stamps on `<h2>`.
 *
 * Shared by the extractor and the renderer so the nav's section links and the headings they point
 * at cannot drift apart.
 */
export function headingId(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .trim()
    .replace(/\s+/g, '-');
}

export interface DocHeading {
  id: string;
  text: string;
}

/**
 * The `##` headings of a document, for the "in this page" list.
 *
 * Read off the Markdown source rather than the rendered DOM — the list has to exist before the
 * document renders, and reaching into rendered output for it would mean rendering twice. Fenced
 * code blocks are skipped so a `## ` line inside an example prompt isn't mistaken for a section.
 */
export function headingsOf(content: string): DocHeading[] {
  const headings: DocHeading[] = [];
  let inFence = false;

  for (const line of content.split('\n')) {
    if (line.startsWith('```')) {
      inFence = !inFence;
      continue;
    }
    if (inFence) continue;

    const match = /^##\s+(.+?)\s*$/.exec(line);
    if (match) headings.push({ id: headingId(match[1]), text: match[1] });
  }

  return headings;
}
